// @flow weak
/* eslint pinterest/no-translation-outside-of-function:0 */
// Module dependencies
import * as StringUtil from 'app/common/lib/StringUtil';
import GettextUtils from 'app/packages/pinterest-l10n/GettextUtils';
import Logging from 'app/common/lib/Logging';
import { type I18nType } from 'app/common/lib/i18n';

const i18nLogger = Logging.getLogger('string');

function makeI18n(_localeCode: string = 'en-US', _localeData): I18nType {
  const i18n = {};

  _localeData[_localeCode] = _localeData[_localeCode] || {};
  _localeData[_localeCode].i18nStrings =
    _localeData[_localeCode].i18nStrings || _localeData.i18nStrings;
  _localeData[_localeCode].plural = _localeData[_localeCode].plural || _localeData.plural;

  i18n.getPluralFunction = function(localeCode = _localeCode) {
    if (_localeData[localeCode] && _localeData[localeCode].plural) {
      return _localeData[localeCode].plural;
    } else {
      i18nLogger.error('plural function for ' + localeCode + ' not loaded');
      // default to en-US plural function
      return function(n) {
        return n !== 1;
      };
    }
  };

  i18n.getLocaleData = function() {
    return _localeData;
  };

  function _getI18nStrings() {
    if (_localeData[_localeCode] && _localeData[_localeCode].i18nStrings) {
      return _localeData[_localeCode].i18nStrings;
    }
    return {};
  }

  function _isCopytuneActivated() {
    let activated = false;

    if (_localeData.copytuneExperiments) {
      if (
        _localeData.copytuneExperiments.translations &&
        _localeData.experiments &&
        typeof _localeData.experiments.v2ActivateExperiment === 'function'
      ) {
        activated = true;
      } else {
        i18nLogger.error('Copytune is not properly setup, skip it', new Error().stack);
      }
    }

    return activated;
  }

  /**
   * Get a localized string.
   *
   * @param {string} messageId The id of the string.
   * @param {string=} _context An optional string to provide additional context to coptyune.
   * @param {number=} pluralIndex An optional number to indicate which form of a pluralized string to use
   * @return {string} the string if found, otherwise the id string.
   */
  i18n._ = function(messageId, _context, pluralIndex): string {
    // Format the messageId so it matches the dictionary key.
    messageId = messageId.replace(/\s+/g, ' ');

    if (_isCopytuneActivated()) {
      const stringId = GettextUtils.getTextUnitName(messageId, _context, pluralIndex, _localeCode);

      const copytunedTranslation = _localeData.copytuneExperiments.translations[stringId];

      if (copytunedTranslation) {
        const triggerableExperiment = _localeData.copytuneExperiments.triggerable[stringId];

        if (triggerableExperiment) {
          _localeData.experiments.v2ActivateExperiment(triggerableExperiment);
        }

        return copytunedTranslation;
      }
    }

    if (_localeData.pseudoLocalize) {
      return _localeData.pseudoLocalize(messageId);
    }

    const i18nStrings = _getI18nStrings();

    // Set context to the default context('') if none is specified
    if (!_context) {
      _context = '';
    }

    let translation = messageId;

    if (_localeCode === undefined || _localeCode === 'en-US') {
      i18nLogger.debug('When locale is en_US or undefined (for tests) just return original string');
    } else if (i18nStrings === undefined) {
      i18nLogger.warn(`Could not load strings for locale ${_localeCode}.`);
    } else if (i18nStrings[_context] === undefined) {
      i18nLogger.warn(`Context ${_context} could not be found for locale ${_localeCode}.`);
    } else if (i18nStrings[_context][messageId] === undefined) {
      i18nLogger.warn(
        `Translation failed in locale ${_localeCode}. _("${messageId}", ` +
          `${_context}, ${pluralIndex});`,
      );
    } else if (pluralIndex !== undefined) {
      let messageArray = i18nStrings[_context][messageId];
      // Make sure message is an array
      if (!Array.isArray(messageArray)) {
        messageArray = [messageArray];
      }

      if (messageArray[pluralIndex] === undefined) {
        i18nLogger.warn(
          `Pluralization failed in locale ${_localeCode}. _("${messageId}", ` +
            `${_context}, ${pluralIndex});`,
        );
      } else {
        translation = messageArray[pluralIndex];
      }
    } else {
      translation = i18nStrings[_context][messageId];
    }

    if (_localeData.inContextTranslationWebapp) {
      translation = _localeData.inContextTranslationWebapp.getIctTranslationOrDefault(
        messageId,
        _context,
        pluralIndex,
        _localeCode,
        translation,
      );
    }

    return translation;
  };

  /*
   * New ngettext version to be used in react
   * This conforms to the expected ngettext(singular, plural, count) signature
  */
  i18n.ngettext = function(singular, plural, count, context) {
    const localeCode = _localeCode;
    // unary + operator is intentional -- some of the plural expressions return booleans
    // we need an int value to get the array index of the pluralized string
    const pluralIndex = +i18n.getPluralFunction(localeCode)(count);
    let val;
    if (localeCode === 'en-US' && pluralIndex === 1) {
      val = plural;
    } else {
      val = singular;
    }
    return i18n._(val, context, pluralIndex);
  };

  /**
   * These are strings referred to in more than one place.
   * Rather than having multiple instances of the same message,
   * define them here and refer to them by reference.
   * The initialization needs to happen after P.setStrings
   * is called, or strings will not be properly localized.
   */
  i18n.commonStrings = {
    successInviteSentToEmail: i18n._('Success! Invitation sent to {{ email }}'),
    connectError: i18n._("Couldn't connect. You must authorize Pinterest."),
    defaultErrorMessage: i18n._('Sorry! Something went wrong on our end!'),
    asciiPasswordError: i18n._(
      'Oops! Your password can only have letters, numbers and basic symbols.',
    ),
  };

  /**
   * Given a date string or a timestamp, return a human-readable string of how long
   * ago it has been since the time given.
   * @param {!string|number} timeStr The time since which the result is being calculated.
   * @param {Object} options with fields isAbbreviated (if the string should be abbreviated)
   *                 and skipSeconds to return "just now" when difference is only in seconds
   * @return {string} A human-readable string, such as "1 hour ago", "2 weeks ago"
   */
  i18n.getHumanizedTimesince = function(timeStr, options) {
    const { isAbbreviated, skipSeconds } = options || {};
    const oneSecond = 1000;
    const oneMinute = 60 * 1000;
    const oneHour = 60 * oneMinute;
    const oneDay = oneHour * 24;
    const oneWeek = oneDay * 7;
    const oneYear = oneDay * 365; // approximation

    const time = new Date(timeStr);
    const now = new Date();
    const difference = now.getTime() - time.getTime();

    const numSecondsBetween = Math.floor(difference / oneSecond);
    const numMinutesBetween = Math.floor(difference / oneMinute);
    const numHoursBetween = Math.floor(difference / oneHour);
    const numDaysBetween = Math.floor(difference / oneDay);
    const numWeeksBetween = Math.floor(difference / oneWeek);
    const numYearsBetween = Math.floor(difference / oneYear);

    // TODO(chris/tilde): The Jinja version of humanize_timeslice doesn't have month
    // logic.  Once we remove our use of the Jinja humanize_timeslice, consider re-enabling this.
    // var numMonthsBetween = _numMonthsBetween(time, now);

    if (numYearsBetween > 0) {
      if (isAbbreviated) {
        return StringUtil.dangerouslyFormat(i18n._('{{ years }}y', 'Abbreviation for years'), {
          years: `${numYearsBetween}`,
        });
      }
      return StringUtil.dangerouslyFormat(
        i18n.ngettext(
          '{{ years }} year ago',
          '{{ years }} years ago',
          numYearsBetween,
          'describing amount of time that has passed - e.g. in notifications, messages',
        ),
        { years: `${numYearsBetween}` },
      );
    }

    if (numWeeksBetween > 1) {
      if (isAbbreviated) {
        return StringUtil.dangerouslyFormat(i18n._('{{ weeks }}w', 'Abbreviation for weeks'), {
          weeks: `${numWeeksBetween}`,
        });
      }
      return StringUtil.dangerouslyFormat(
        i18n.ngettext(
          '{{ weeks }} week ago',
          '{{ weeks }} weeks ago',
          numWeeksBetween,
          'describing amount of time that has passed - e.g. in notifications, messages',
        ),
        { weeks: `${numWeeksBetween}` },
      );
    }

    if (numDaysBetween > 0) {
      if (isAbbreviated) {
        return StringUtil.dangerouslyFormat(i18n._('{{ days }}d', 'Abbreviation for days'), {
          days: `${numDaysBetween}`,
        });
      }
      return StringUtil.dangerouslyFormat(
        i18n.ngettext(
          '{{ days }} day ago',
          '{{ days }} days ago',
          numDaysBetween,
          'describing amount of time that has passed - e.g. in notifications, messages',
        ),
        { days: `${numDaysBetween}` },
      );
    }

    if (numHoursBetween > 0) {
      if (isAbbreviated) {
        return StringUtil.dangerouslyFormat(i18n._('{{ hours }}h', 'Abbreviation for hours'), {
          hours: `${numHoursBetween}`,
        });
      }
      return StringUtil.dangerouslyFormat(
        i18n.ngettext(
          '{{ hours }} hour ago',
          '{{ hours }} hours ago',
          numHoursBetween,
          'describing amount of time that has passed - e.g. in notifications, messages',
        ),
        { hours: `${numHoursBetween}` },
      );
    }

    if (numMinutesBetween > 0) {
      if (isAbbreviated) {
        return StringUtil.dangerouslyFormat(i18n._('{{ minutes }}m', 'Abbreviation for minutes'), {
          minutes: `${numMinutesBetween}`,
        });
      }
      return StringUtil.dangerouslyFormat(
        i18n.ngettext(
          '{{ minutes }} minute ago',
          '{{ minutes }} minutes ago',
          numMinutesBetween,
          'describing amount of time that has passed - e.g. in notifications, messages',
        ),
        { minutes: `${numMinutesBetween}` },
      );
    }

    if (numSecondsBetween >= 0 && !skipSeconds) {
      if (isAbbreviated) {
        return StringUtil.dangerouslyFormat(
          i18n._('{{ numSecondsBetween }}s', 'Abbreviation for seconds'),
          {
            numSecondsBetween: `${numSecondsBetween}`,
          },
        );
      }
      return StringUtil.dangerouslyFormat(
        i18n.ngettext(
          '{{ numSecondsBetween }} second ago',
          '{{ numSecondsBetween }} seconds ago',
          numSecondsBetween,
          'describing amount of time that has passed - e.g. in notifications, messages',
        ),
        { numSecondsBetween: `${numSecondsBetween}` },
      );
    }

    return i18n._(
      'just now',
      'describing amount of time that has passed - e.g. in notifications, messages',
    );
  };

  return ((i18n: any): I18nType);
}

export default makeI18n;
